import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Badge, Menu, MenuItem, Typography, Divider, CircularProgress, Drawer, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuIcon from '@mui/icons-material/Menu';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PostAddIcon from '@mui/icons-material/PostAdd';
import DashboardSidebar from './DashboardSidebar';
import Footer from './Footer';
import UserAccountDropdown from '../UserAccountDropdown/UserAccountDropdown';
import GMBNotificationService from '../../services/GMBNotificationService';
import PermissionStatus from '../PermissionStatus';
import logo from '../../assets/VisibeenLogo.png';
// useAccessGate and AccessDenied are not used in this layout
// Trial offer snackbar removed per request

import logger from '../../utils/logger';
import api from '../../services/api';
const DashboardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#F8F8F8',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column'
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: '#0B91D6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '80px',
  padding: '0 30px',
  [theme.breakpoints.down('md')]: {
    height: '64px',
    padding: '0 20px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '56px',
    padding: '0 16px'
  }
}));

const HeaderLogo = styled('img')(({ theme }) => ({
  height: '40px',
  width: 'auto',
  cursor: 'pointer',
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 0.8
  },
  [theme.breakpoints.down('md')]: {
    height: '32px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '28px'
  }
}));

const MainSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  minHeight: 'calc(100vh - 80px)',
  [theme.breakpoints.down('md')]: {
    minHeight: 'calc(100vh - 64px)'
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 'calc(100vh - 56px)'
  }
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '32px 40px',
  backgroundColor: '#F8F8F8',
  [theme.breakpoints.down('md')]: {
    padding: '24px 24px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 16px'
  }
}));

const HeaderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  [theme.breakpoints.down('sm')]: {
    gap: '8px'
  }
}));

const NotificationButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  }
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  display: 'none',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex'
  }
}));

const SidebarWrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const NotificationMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: '8px',
    minWidth: '320px',
    maxWidth: '400px',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: '1px solid #E5E7EB'
  }
}));

const NotificationItem = styled(MenuItem)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: '1px solid #F3F4F6',
  '&:last-child': {
    borderBottom: 'none'
  },
  '&:hover': {
    backgroundColor: '#F8F9FA'
  }
}));

const NotificationHeader = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderBottom: '1px solid #E5E7EB',
  backgroundColor: '#F8F9FA'
}));

const NotificationTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '16px',
  color: '#1F2937'
}));

const NotificationText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#4B5563',
  marginBottom: '4px'
}));

const NotificationTime = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: '#9CA3AF'
}));

const UnreadDot = styled(Box)(({ theme }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#EF4444',
  marginRight: '8px',
  flexShrink: 0
}));

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  // removed unused access gate checks
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [trialSession, setTrialSession] = useState(null);
  const notificationOpen = Boolean(notificationAnchor);
  

  // Clear cached profile selection when in admin access mode to prevent showing stale data
  useEffect(() => {
    const adminAccessData = sessionStorage.getItem('adminAccessData');
    if (adminAccessData) {
      // Admin is viewing a client - clear cached profile selection to force fresh load
      localStorage.removeItem('selectedProfile');
      logger.log('[DashboardLayout] Admin access detected - cleared cached profile selection');
    }
  }, []);

  // Keep trialSession in sync with storage events and explicit signals
  useEffect(() => {
    let mounted = true;

    // Server-authoritative: do not show trial UI from local client flags alone.
    // We'll fetch `/customer/auth/trial-session` immediately to determine state.

    const verifyServer = async () => {
      try {
      console.log('[DashboardLayout] verifying server trial-session...');
      const resp = await api.get('/customer/auth/trial-session');
      console.log('[DashboardLayout] /trial-session response:', resp);
      const serverInvite = resp && (resp.trialInvite || (resp.data && resp.data.trialInvite)) ? (resp.trialInvite || resp.data.trialInvite) : null;
      if (!mounted) return;
      console.log('[DashboardLayout] resolved serverInvite:', serverInvite);
      setTrialSession(serverInvite);
      if (!serverInvite) {
        try { sessionStorage.removeItem('isTrialSession'); } catch(_) {}
        try { localStorage.removeItem('isTrialSession'); } catch(_) {}
      }
      } catch (err) {
        if (!mounted) return;
        setTrialSession(null);
      }
    };

    const handleSignal = async () => {
      try {
        // If there is a local trial invite flag, re-check server so server-side cookie is authoritative
        const inviteRaw = (sessionStorage.getItem('trial_invite') || localStorage.getItem('trial_invite'));
        const tokenFallback = (sessionStorage.getItem('came_from_trial') || localStorage.getItem('came_from_trial') || localStorage.getItem('came_from_trial_token'));
        console.log('[DashboardLayout] handleSignal fired. inviteRaw=', !!inviteRaw, 'tokenFallback=', !!tokenFallback);
        if (inviteRaw || tokenFallback) {
          await verifyServer();
          return;
        }
        // nothing locally present -> clear
        setTrialSession(null);
      } catch (_) {
        // ignore
      }
    };

    // Initial authoritative check
    verifyServer();

    const storageHandler = (e) => {
      if (!e || !e.key) return;
      if (e.key === 'trial_invite' || e.key.startsWith('came_from_trial')) {
        handleSignal();
      }
    };

    window.addEventListener('trialInviteSet', handleSignal);
    window.addEventListener('storage', storageHandler);
    return () => {
      mounted = false;
      window.removeEventListener('trialInviteSet', handleSignal);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  // Trial offer snackbar disabled

  // Fetch real notifications from GMB
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Get Google access token from localStorage
      const accessToken = localStorage.getItem('googleAccessToken');

      if (!accessToken) {
        logger.log('[DashboardLayout] No access token available for notifications');
        setNotifications([]);
        return;
      }

      // Get selected business from localStorage
      const selectedBusiness = localStorage.getItem('selectedBusiness');
      if (!selectedBusiness) {
        logger.log('[DashboardLayout] No business selected');
        setNotifications([]);
        return;
      }

      const business = JSON.parse(selectedBusiness);
      const locationId = business.name?.split('/').pop();

      if (!locationId) {
        logger.log('[DashboardLayout] No location ID found');
        setNotifications([]);
        return;
      }

      logger.log('[DashboardLayout] Fetching notifications for location:', locationId);
      const recentNotifications = await GMBNotificationService.getRecentNotifications(accessToken, locationId);

      logger.log('[DashboardLayout] Received notifications:', recentNotifications);

      // Transform notifications to match UI format
      const transformedNotifications = recentNotifications.map(notif => ({
        id: notif.id,
        title: notif.title,
        message: notif.message,
        time: notif.timestamp,
        unread: notif.unread !== false,
        type: notif.type,
        icon: notif.icon
      }));

      setNotifications(transformedNotifications);
      setLastFetchTime(new Date());
    } catch (error) {
      logger.error('[DashboardLayout] Error fetching notifications:', error);
      // Set empty array on error to avoid showing stale data
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notifications on mount and when notification menu opens
  useEffect(() => {
    fetchNotifications();

    // Check server-side trial session to show UI indicator
    (async () => {
      try {
        const resp = await api.get('/customer/auth/trial-session');
        const serverInvite = resp && (resp.trialInvite || (resp.data && resp.data.trialInvite)) ? (resp.trialInvite || resp.data.trialInvite) : null;
        if (serverInvite) setTrialSession(serverInvite);
      } catch (e) {
        // ignore
      }
    })();

    // Set up periodic refresh every 5 minutes
    const interval = setInterval(() => {
      fetchNotifications();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const unreadCount = notifications.filter(n => n.unread).length;

  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
    // Refresh notifications when opening the menu
    if (!lastFetchTime || (new Date() - lastFetchTime) > 60000) {
      fetchNotifications();
    }
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleNotificationItemClick = (notification) => {
    // Mark as read
    setNotifications(prev =>
      prev.map(n => n.id === notification.id ? { ...n, unread: false } : n)
    );

    // Navigate based on notification type
    if (notification.type === 'NEW_REVIEW' || notification.type === 'UPDATED_REVIEW') {
      window.location.href = '/reviews-management';
    } else if (notification.type === 'NEW_CUSTOMER_MEDIA') {
      window.location.href = '/business-profile';
    } else if (notification.type === 'NEW_QUESTION' || notification.type === 'UPDATED_QUESTION') {
      window.location.href = '/business-profile';
    }

    handleNotificationClose();
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'NEW_REVIEW':
      case 'UPDATED_REVIEW':
        return <RateReviewIcon sx={{ fontSize: 20, color: '#0B91D6', mr: 1 }} />;
      case 'NEW_CUSTOMER_MEDIA':
        return <PhotoLibraryIcon sx={{ fontSize: 20, color: '#10B981', mr: 1 }} />;
      case 'NEW_QUESTION':
      case 'UPDATED_QUESTION':
      case 'NEW_GOOGLE_UPDATE':
        return <PostAddIcon sx={{ fontSize: 20, color: '#F59E0B', mr: 1 }} />;
      default:
        return <NotificationsIcon sx={{ fontSize: 20, color: '#6B7280', mr: 1 }} />;
    }
  };

  // Non-whitelisted users can still see the dashboard but sidebar is restricted
  // The sidebar component handles showing limited options for non-whitelisted users

  return (
    <DashboardContainer>
      <PermissionStatus />
      <HeaderSection>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <MenuButton
            onClick={handleMobileMenuToggle}
            aria-label="menu"
          >
            <MenuIcon />
          </MenuButton>
          <HeaderLogo
            src={logo}
            alt="Visibeen"
            onClick={() => navigate('/dashboard')}
            title="Go to Dashboard"
          />
        </Box>
        <HeaderActions>
          {trialSession ? (
            <Tooltip title={"Trial active"}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#10B981' }} />
                <Typography sx={{ color: '#10B981', fontWeight: 700, fontSize: 13 }}>Trial</Typography>
              </Box>
            </Tooltip>
          ) : null}
          <NotificationButton
            onClick={handleNotificationClick}
            aria-label={`${unreadCount} notifications`}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </NotificationButton>
          <UserAccountDropdown />
        </HeaderActions>
      </HeaderSection>

      <NotificationMenu
        anchorEl={notificationAnchor}
        open={notificationOpen}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <NotificationHeader>
          <NotificationTitle>Notifications</NotificationTitle>
        </NotificationHeader>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length > 0 ? (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              onClick={() => handleNotificationItemClick(notification)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                {notification.unread && <UnreadDot />}
                {getNotificationIcon(notification.type)}
                <Box sx={{ flex: 1 }}>
                  <NotificationText sx={{ fontWeight: notification.unread ? 600 : 400 }}>
                    {notification.title}
                  </NotificationText>
                  <NotificationText>
                    {notification.message}
                  </NotificationText>
                  <NotificationTime>
                    {notification.time}
                  </NotificationTime>
                </Box>
              </Box>
            </NotificationItem>
          ))
        ) : (
          <NotificationItem>
            <NotificationText sx={{ textAlign: 'center', color: '#9CA3AF' }}>
              No new notifications
            </NotificationText>
          </NotificationItem>
        )}
        <Divider />
        <NotificationItem sx={{ justifyContent: 'center', color: '#0B91D6', fontWeight: 600 }}>
          View All Notifications
        </NotificationItem>
      </NotificationMenu>

      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '280px',
            maxWidth: '85vw',
            boxSizing: 'border-box',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DashboardSidebar onNavigate={handleMobileMenuClose} />
      </Drawer>

      <MainSection>
        <SidebarWrapper>
          <DashboardSidebar />
        </SidebarWrapper>
        <ContentArea>
          {children}
        </ContentArea>
      </MainSection>
      {/* TrialOfferSnackbar removed */}
      <Footer />
    </DashboardContainer>
  );
};

export default DashboardLayout;